import React from 'react';
import PropTypes from 'prop-types';
import { Toast, } from '@payit/cool-web-ui-library';
import StringUtils from '../../modules/StringUtils';
import HtmlUtils from '../../modules/HtmlUtils';
import './Table.css';

class Table extends React.Component {
  static DOUBLE_CLICK_TIMEOUT = 500;

  static MAX_TOAST_LENGTH = 50;

  constructor(props) {
    super(props);
    this.state = {
      sorter: {
        count: 0,
        sort: (a) => a,
      },
      rows: props.rows,
      headers: props.headers,
    };
    this.clickTimeout = null;
    this.lastClickedCell = null;
  }

  onSort(header) {
    const { headers, sorter, } = this.state;
    let index = null;
    headers.forEach((element, i) => {
      if (element === header) {
        index = i;
      }
    });

    this.setState({
      sorter: this.buildSorter(header, sorter, index),
    });
  }

  onRowSelectEvent(event) {
    const { headers = [], onRowSelect, } = this.props;
    const row = {};
    const size = headers.length;
    const element = event.currentTarget;

    for (let i = 0; i < size; i += 1) {
      const value = element.childNodes[i];
      const header = headers[i];
      if (header && header.trim().length > 0) {
        row[headers[i]] = value?.innerText ?? '--';
      }
    }
    if (onRowSelect) {
      onRowSelect(row);
    }
  }

  buildSorter(header, sorter, index) {
    const copySorter = { ...sorter, };
    const ASCENDING = 'ASCENDING';
    const DESCENDING = 'DESCENDING';

    const ascending = (rows) => rows.slice().sort((a, b) => {
      const aValue = StringUtils.toEmptyStringOnVoid(a[index]);
      const bValue = StringUtils.toEmptyStringOnVoid(b[index]);

      if (aValue > bValue) {
        return 1;
      }
      if (aValue < bValue) {
        return -1;
      }
      return 0;
    });

    const descending = (rows) => rows.slice().sort((a, b) => {
      const aValue = StringUtils.toEmptyStringOnVoid(a[index]);
      const bValue = StringUtils.toEmptyStringOnVoid(b[index]);

      if (aValue > bValue) {
        return -1;
      }
      if (aValue < bValue) {
        return 1;
      }
      return 0;
    });

    if (header === copySorter.key) {
      if (copySorter.direction === ASCENDING) {
        copySorter.sort = descending;
        copySorter.direction = DESCENDING;
      } else {
        copySorter.sort = ascending;
        copySorter.direction = ASCENDING;
      }
    } else {
      copySorter.sort = ascending;
      copySorter.direction = ASCENDING;
      copySorter.key = header;
    }
    return copySorter;
  }

  handleCopy = (cell, event) => {
    const trElement = event.currentTarget.parentElement;
    const newEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    Object.defineProperty(newEvent, 'currentTarget', { value: trElement, });

    this.onRowSelectEvent(newEvent);
    event.stopPropagation();

    const content = HtmlUtils.toHtmlText(cell);
    navigator.clipboard.writeText(content);

    const displayContent = content.length > Table.MAX_TOAST_LENGTH
      ? `${content.substring(0, Table.MAX_TOAST_LENGTH)}...`
      : content;

    Toast.showToast({
      contentType: Toast.CONTENT_TYPES.SUCCESS,
      autoHide: true,
      autoHideDelay: 2000,
      text: 'Copied to clipboard',
      detail: `Copied: ${displayContent}`,
    });
  };

  handleCellClick = (cell, event) => {
    // Always do row selection regardless of cell content
    const trElement = event.currentTarget.parentElement;
    const newEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    Object.defineProperty(newEvent, 'currentTarget', { value: trElement, });

    this.onRowSelectEvent(newEvent);

    // Only handle copy functionality for non-empty cells
    if (cell) {
      if (this.clickTimeout && this.lastClickedCell === cell) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
        this.lastClickedCell = null;
        this.handleCopy(cell, event);
      } else {
        this.lastClickedCell = cell;
        this.clickTimeout = setTimeout(() => {
          this.clickTimeout = null;
          this.lastClickedCell = null;
        }, Table.DOUBLE_CLICK_TIMEOUT);
      }
    }
  };

  render() {
    const { headers, rows, sorter, } = this.state;
    const { className, } = this.props;

    return (
      <>
        <Toast />
        <div className="protip">
          Hover over and double click any data cell to copy its contents to your
          clipboard.
        </div>
        <table className={className}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={`header-${index}`} onClick={() => this.onSort(header)}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorter.sort(rows).map((row, rowIndex) => {
              if (row.length === 0) {
                return <tr key={`empty-row-${rowIndex}`} />;
              }
              return (
                <tr key={`row-${rowIndex}`} tabIndex={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={`cell-${rowIndex}-${cellIndex}`}
                      onClick={(event) => this.handleCellClick(cell, event)}
                      className={cell ? 'copyable-cell' : ''}
                    >
                      {HtmlUtils.toHtmlText(cell)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }
}

Table.defaultProps = {
  className: '',
  onRowSelect: () => {},
};

Table.propTypes = {
  headers: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  className: PropTypes.string,
  onRowSelect: PropTypes.func,
};

export default Table;
